import { createAtlAttributionHeader } from '../../utils';
import { type ProductContextKey } from './lib/realtime';
import { commitMutation, graphql, useRelayEnvironment } from 'react-relay';
import type { usePublishRealtimeChannelMutation } from './__generated__/usePublishRealtimeChannelMutation.graphql';

export type PublishRealtimeChannelPayload = {
	data: {
		channelName: string;
		eventPayload: string | Record<string, unknown>;
		isGlobal?: boolean;
		options?: {
			contextOverrides?: ProductContextKey[];
			token?: string;
		};
	};
};

export type PublishRealtimeChannel = (
	payload: PublishRealtimeChannelPayload,
	contextToken?: string,
) => Promise<{ eventId: string; eventTimestamp: string } | undefined>;

export const usePublishRealtimeChannel = (installationId: string, context: string) => {
	const relayEnvironment = useRelayEnvironment();

	return async (
		payload: PublishRealtimeChannelPayload,
		contextToken?: string,
	): Promise<
		| {
				eventId: string;
				eventTimestamp: string;
		  }
		| undefined
	> => {
		const { channelName, isGlobal, options } = payload.data;

		const { token, contextOverrides } = options || {};

		const eventPayload = JSON.stringify(payload.data.eventPayload);
		const channelContext = contextOverrides
			? JSON.stringify({
					context,
					contextOverrides,
				})
			: context;

		return new Promise<{ eventId: string; eventTimestamp: string } | undefined>(
			(resolve, reject) => {
				commitMutation<usePublishRealtimeChannelMutation>(relayEnvironment, {
					mutation: graphql`
						mutation usePublishRealtimeChannelMutation(
							$installationId: ID!
							$name: String!
							$payload: String!
							$context: String
							$isGlobal: Boolean
							$token: String
						) {
							ecosystem {
								publishRealtimeChannel(
									installationId: $installationId
									name: $name
									payload: $payload
									context: $context
									isGlobal: $isGlobal
									token: $token
								) {
									eventId
									eventTimestamp
								}
							}
						}
					`,
					variables: {
						installationId,
						name: channelName,
						payload: eventPayload,
						context: channelContext,
						isGlobal: isGlobal ?? false,
						token,
					},
					cacheConfig: {
						metadata: {
							headers: {
								'atl-attribution': createAtlAttributionHeader(),
								...(contextToken ? { 'x-forge-context-token': contextToken } : {}),
							},
						},
					},
					onCompleted: (data, errors) => {
						if (errors) {
							reject(errors);
							return;
						}

						if (data?.ecosystem?.publishRealtimeChannel) {
							const { eventId, eventTimestamp } = data.ecosystem.publishRealtimeChannel;
							if (eventId && eventTimestamp) {
								resolve({ eventId, eventTimestamp });
							} else {
								resolve(undefined);
							}
						} else {
							resolve(undefined);
						}
					},
					onError: reject,
				});
			},
		);
	};
};
